'use client';

import { twMerge } from '@shared/themes';
import { useMemo, type ComponentProps } from 'react';
import { staticGameFilters } from '../../../../filters/staticFilters';
import type { GamesFiltersType } from '../../../../GamesFilter/GamesFilter';
import { useGameFilters } from '../../../../GamesFilter/hooks/useGameFilters';
import { Tag, type TagValue } from '../../../../Tag/Tag';
import { useGamesList } from '../../../context/GamesListProvider/GamesListProvider';

type TagWithCategory = TagValue & {
  filterName: keyof GamesFiltersType;
};

type TagsContainerProps = ComponentProps<'div'> & {
  className?: string;
};

const FILTER_TRACK_NAMES: Record<keyof GamesFiltersType, string> = {
  sorting: 'Sorting',
  volatility: 'Volatility',
  minimumBet: 'Minimum Bet',
  gameProviders: 'Providers',
};

function TagsContainer({ className, ...props }: TagsContainerProps) {
  const { gameFilters, updateGameFilters } = useGameFilters();
  const { availableGameProviders } = useGamesList();

  const tags = useMemo(() => {
    const mapFiltersToTags = (filters: GamesFiltersType) => {
      const tags: TagWithCategory[] = [];

      const getVolatilityLabel = () => {
        return `${filters.volatility?.min}-${filters.volatility?.max}`;
      };

      const getVolatilityValue = () => {
        return `${filters.volatility!.min}-${filters.volatility!.max}`;
      };

      if (gameFilters.gameProviders) {
        tags.push(
          ...gameFilters.gameProviders.map((value: string) => ({
            label: availableGameProviders.find(provider => provider.value === value)?.label || value,
            value: value,
            filterName: 'gameProviders' as keyof GamesFiltersType,
          }))
        );
      }
      if (gameFilters.volatility) {
        tags.push({ label: getVolatilityLabel(), value: getVolatilityValue(), filterName: 'volatility' });
      }
      if (gameFilters.minimumBet) {
        tags.push(
          ...gameFilters.minimumBet.map((value: string) => ({
            label: staticGameFilters.minimumBetChips.find(chip => chip.value === value)?.label || value,
            value: value,
            filterName: 'minimumBet' as keyof GamesFiltersType,
          }))
        );
      }

      return tags;
    };

    return mapFiltersToTags(gameFilters);
  }, [availableGameProviders, gameFilters]);

  const handleRemove = (tag: { value: string; filterName: keyof GamesFiltersType }) => {
    const updatedFilters = { ...gameFilters };
    if (tag.filterName === 'volatility') {
      delete updatedFilters.volatility;
    } else {
      // @ts-expect-error - types problem with filterName
      updatedFilters[tag.filterName] = (updatedFilters[tag.filterName] as string[]).filter((value: string) => value !== tag.value);
    }
    updateGameFilters(updatedFilters);
  };

  return (
    <div
      className={twMerge('flex items-center gap-2 flex-row-reverse md:flex-wrap justify-start overflow-x-scroll hide-scrollbar', tags.length > 0 ? 'mt-6' : '', className)}
      {...props}
    >
      {tags.map(({ label, value, filterName }) => (
        <Tag
          className="shrink-0"
          label={label}
          key={value}
          value={value}
          onRemove={() => handleRemove({ value, filterName })}
          data-track-container={FILTER_TRACK_NAMES[filterName] || ''}
          data-track-name={label}
          data-track-selected="Yes"
        />
      ))}
    </div>
  );
}

export { TagsContainer };
